<template>
  <div class="register-interest">
    <FeedbackDialog
      v-if="!isAvailableSlots"
      data-testid="no-available-slots"
      class="register-interest__no-available-slots-dialog"
      message="Sorry, we currently do not have any available slots"
      header="No Available Slots"
      variant="warning"
    />
    <p class="register-interest__title">Tell us when you’re available</p>
    <p>
      Let us know your usual availability and we’ll email you as soon as
      matching appointments become available.
    </p>
    <div class="register-interest__days-container">
      <Spinner
        v-if="slotsPending"
        spinner-text="Loading..."
        class="register-interest__spinner"
      />
      <div
        v-for="(slots, day) in settings"
        v-else
        :key="day"
        class="register-interest__day-row"
      >
        <div class="register-interest__slots">
          <div
            v-for="(value, period) in slots"
            :key="period"
            class="register-interest__slot"
            data-testid="slots"
          >
            <Checkbox
              v-model="settings[day][period]"
              :label="slotName(day, period)"
              :input-id="day + '-' + period"
              :data-testid="day + '-' + period"
              :has-error="isNoSlotsSelected"
              checkbox-card
              full-width
              @change="onSlotClick"
            />
          </div>
        </div>
      </div>
      <FeedbackDialog
        v-if="isNoSlotsSelected"
        class="register-interest__no-slots-dialog"
        variant="negative"
        full-width
      >
        Please select at least one availability time slot to continue
      </FeedbackDialog>
      <InputField
        v-model="comment"
        class="register-interest__comment"
        label="Additional notes e.g. any dates that you will be away."
        name="registerInterestComment"
        force-label-up
        multiline
        autofocus
        full-width
      />
      <AppButton
        class="register-interest__button"
        full-width
        @click="saveInterest"
      >
        <span>Continue</span>
        <span class="register-interest__icon icon icon-arrow-right" />
      </AppButton>
    </div>
  </div>
</template>

<script>
import GatewayPortal from "@/services/GatewayPortal"
import AppButton from "@soenergy/frontend-library/src/components/AppButton.vue"
import Checkbox from "@soenergy/frontend-library/src/components/Checkbox.vue"
import FeedbackDialog from "@soenergy/frontend-library/src/components/FeedbackDialog.vue"
import InputField from "@soenergy/frontend-library/src/components/InputField.vue"
import Spinner from "@soenergy/frontend-library/src/components/Spinner.vue"
import { mapGetters } from "vuex"

export default {
  components: {
    FeedbackDialog,
    AppButton,
    InputField,
    Spinner,
    Checkbox,
  },

  data() {
    return {
      slotsPending: false,
      settings: {
        monday: {
          am: false,
          pm: false,
        },
        tuesday: {
          am: false,
          pm: false,
        },
        wednesday: {
          am: false,
          pm: false,
        },
        thursday: {
          am: false,
          pm: false,
        },
        friday: {
          am: false,
          pm: false,
        },
      },
      comment: "",
      isNoSlotsSelected: false,
    }
  },
  computed: {
    ...mapGetters(["postcode", "isAvailableSlots"]),
    availabilitySlots() {
      const days = Object.keys(this.settings)
      const slots = []
      days.forEach((day) => {
        const allSlots = Object.keys(this.settings[day])
        let chosenSlots = ""
        allSlots.forEach((slot) => {
          chosenSlots += this.settings[day][slot]
            ? `${slot.toUpperCase()}, `
            : ""
        })
        if (chosenSlots.length) {
          slots.push(
            `${this.capitalisedDay(day)} ${chosenSlots.slice(
              0,
              chosenSlots.length - 2
            )}`
          )
        }
      })
      return slots
    },
  },
  mounted() {
    this.fetchInterest()
  },
  methods: {
    capitalisedDay(day) {
      return `${day.charAt(0).toUpperCase()}${day.slice(1)}`
    },
    slotName(day, period) {
      return `${this.capitalisedDay(day)} ${period.toUpperCase()}`
    },
    onSlotClick() {
      this.isNoSlotsSelected = false
    },
    fetchInterest() {
      this.slotsPending = true
      this.isNoSlotsSelected = false
      GatewayPortal.getCustomerAvailability(this.$store.state.customerId)
        .then((response) => {
          if (response.data.interest) {
            this.settings = response.data.interest
          }
          if (response.data.comment) {
            this.comment = response.data.comment
          }
        })
        .catch((err) => {
          throw err
        })
        .finally(() => {
          this.slotsPending = false
        })
    },
    saveInterest() {
      this.isNoSlotsSelected = this.availabilitySlots.length === 0
      if (this.isNoSlotsSelected) {
        return
      }
      const data = {
        interest: this.settings,
        comment: this.comment,
        postcode: this.postcode,
        source: "SMBP",
      }
      this.slotsPending = true
      GatewayPortal.setCustomerAvailability(this.$store.state.customerId, data)
        .then(() => {
          this.$store.commit("SET_AVAILABILITY_SLOTS", this.availabilitySlots)
          this.$emit("go-forward")
        })
        .finally(() => {
          this.slotsPending = true
        })
    },
  },
}
</script>

<style scoped lang="scss">
.register-interest {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: $space-5;

  &__icon {
    margin-left: $space-1;
  }

  &__no-slots-dialog {
    margin-top: $space-6;
  }
  &__no-available-slots-dialog {
    margin-bottom: $space-5;
    width: 100%;
  }
  &__spinner {
    margin-left: 35%;
  }

  &__title {
    font-size: $font-size-5;
    font-weight: $weight-medium;
    margin-bottom: $space-4;

    @include md {
      font-size: $font-size-6;
    }
  }

  &__days-container {
    display: flex;
    flex-wrap: wrap;
    margin-top: $space-3;
  }

  &__day-row {
    display: flex;
    width: 100%;
  }

  &__slots {
    display: grid;
    width: 100%;
    @media (min-width: $screen-md-min) {
      grid-template-columns: 1fr 1fr;
      gap: $space-3;
    }
    @media (max-width: #{$screen-md-min - 1}) {
      grid-template-columns: 1fr;
    }
  }

  &__slot {
    margin-top: $space-3;
  }

  &__button {
    width: 100%;
    margin-top: $space-1;
    .button {
      position: fixed;
      bottom: $space-4;
      left: 50%;
      transform: translateX(-50%);
      z-index: zindex(sticky);
      width: 90%;

      @include md {
        width: 100%;
        position: static;
        transform: translateX(0);
        margin-top: $space-8;
      }
    }
  }
  &__comment {
    margin-top: $space-8;
    width: 100%;
  }
}
</style>
